import { Text } from "@shared/designSystem/Texts";
import styles from "@shared/styles/components/MilestoneSteps.module.css";
import type { CSSProperties } from "react";

export type MilestoneStepState = "pending" | "current" | "completed";

export interface MilestoneStep {
    id: string;
    title: string;
    state?: MilestoneStepState;
    indicator?: string | number;
}

interface MilestoneStepsProps {
    milestones: MilestoneStep[];
    currentMilestoneId?: string;
    currentMilestoneIndex?: number;
    description?: string;
    ariaLabel?: string;
    className?: string;
}

export const MilestoneSteps = ({
    milestones,
    currentMilestoneId,
    currentMilestoneIndex,
    description,
    ariaLabel = "Milestones",
    className
}: MilestoneStepsProps) => {
    const stepsStyle = {
        "--milestone-count": Math.max(milestones.length, 1)
    } as CSSProperties;
    const currentIndex = getCurrentMilestoneIndex(milestones, currentMilestoneId, currentMilestoneIndex);

    return (
        <div className={["DSMilestoneSteps", styles.container, className].filter(Boolean).join(" ")}>
            <ol className={styles.steps} style={stepsStyle} aria-label={ariaLabel}>
                {milestones.map((milestone, index) => {
                    const state = getMilestoneState(milestone, index, currentIndex);
                    const isLast = index === milestones.length - 1;

                    return (
                        <li
                            key={milestone.id}
                            className={[
                                styles.step,
                                styles[state],
                                index === 0 ? styles.first : "",
                                isLast ? styles.last : ""
                            ].filter(Boolean).join(" ")}
                        >
                            <div className={styles.markerRow} aria-hidden="true">
                                <span className={styles.marker}>
                                    {milestone.indicator ?? index + 1}
                                </span>
                            </div>
                            <Text
                                text={milestone.title}
                                alignment="center"
                                className={styles.title}
                            />
                        </li>
                    );
                })}
            </ol>
            {description && (
                <Text
                    text={description}
                    alignment="center"
                    color="var(--color-text-secondary)"
                    className={styles.description}
                />
            )}
        </div>
    );
};

const getCurrentMilestoneIndex = (
    milestones: MilestoneStep[],
    currentMilestoneId?: string,
    currentMilestoneIndex?: number
): number | undefined => {
    if (currentMilestoneId) {
        const index = milestones.findIndex((milestone) => milestone.id === currentMilestoneId);
        return index >= 0 ? index : undefined;
    }

    if (currentMilestoneIndex !== undefined) {
        return Math.min(Math.max(currentMilestoneIndex, 0), milestones.length - 1);
    }

    return undefined;
};

const getMilestoneState = (
    milestone: MilestoneStep,
    index: number,
    currentIndex?: number
): MilestoneStepState => {
    if (currentIndex === undefined) {
        return milestone.state ?? "pending";
    }

    if (index < currentIndex) {
        return "completed";
    }

    if (index === currentIndex) {
        return "current";
    }

    return "pending";
};
